import { OverlayModule } from '@angular/cdk/overlay';
import { AsyncPipe, LowerCasePipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DoCheck,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ClientPath } from '@shared/models/client-path.enum';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { GlobalNavData } from '@shared/models/global-nav.model';
import { MenuList } from '@shared/models/menu-list';
import { StickyHeaderPipe } from '@shared/pipes/sticky-header';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { OauthService } from '@shared/services/oauth.service';
import { PreloginService } from '@shared/services/prelogin.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { EligibilitiesAndSelfRegEnum } from '@shared/shared/enums/eligibilities-and-selfregs.enum';
import { BaseComponent } from '@shared/shared/helpers/base-component';
import { CLIENT_PATH } from '@shared/shared/injection-tokens';
import { ISiteListDetails, ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { EligibilityRegistrationService } from '@shared/shared/services/eligibility-registration.service';
import { LanguageService } from '@shared/shared/services/language-service';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import * as fromGlobalStore from '@shared/store';
import { isMobile, isTablet } from '@shared/utils/platform';
import { Angulartics2Segment } from 'angulartics2';
import { USER_LOCAL_STORAGE_KEY, USER_SELECTED_SITE } from 'app.config';
import { NavigationService } from 'core/services/navigation.service';
import { debounce } from 'core/utils/debounce';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { slideInOut } from '../../../shared/shared/components/expandable-card/animations';
import { CountrySelectorComponent } from '../country-selector/country-selector.component';
import { MobileNavComponent } from '../mobile-nav/mobile-nav.component';

@Component({
  selector: 'ten-logged-out-nav-content',
  templateUrl: './logged-out-nav-content.component.html',
  styleUrls: ['./logged-out-nav-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [slideInOut()],
  standalone: true,
  imports: [
    NgClass,
    StickyHeaderPipe,
    TranslateModule,
    NgIf,
    RouterModule,
    AccessibleClickDirective,
    AsyncPipe,
    MatLegacyButtonModule,
    NgTemplateOutlet,
    TourMatMenuModule,
    MobileNavComponent,
    OverlayModule,
    MatIconModule,
    LowerCasePipe,
    CountrySelectorComponent,
    NgFor,
  ],
})
export class LoggedOutNavContentComponent extends BaseComponent implements OnInit, OnDestroy, DoCheck {
  public scrollY = 0;
  @HostListener('window:scroll', ['$event'])
  @debounce(80)
  public onScroll() {
    this.scrollY = window.scrollY;
  }

  /**
   * Visibility flag for platform provider information.
   */
  public showPlatformProvider = false;

  /**
   * Platform provider information type.
   */
  public platformProviderType = '';

  public onDestroy$ = new Subject<void>();
  private oauthActive: boolean;
  public componentData: GlobalNavData;
  public showLogInBtn = true;
  public clientName: string;
  public skipLinkPath: string;
  public siteLogo$: Observable<string>;

  private hideLogInBtnRoutes = ['/selector', '/referral'];
  private oauthLogInUrl: string;
  public userLoggedIn = false;
  public auth_provider: string;
  public isAccessHrt: boolean;
  public applyMobileNav = false;

  public showMobileNavOverlay = false;
  public menuState: string;
  public siteSettingsLoaded = false;
  public eligibilityType: string;
  public showSelfReg = false; // Centurion self Reg variable
  public menuList$: Observable<MenuList[]>;
  public pathname = window['location']['pathname'];
  public currentRoute: string;
  public showRegisterButton = false;
  public customisableVariables$: Observable<CustomisableVariables>;
  public isUserLoggedIn: boolean;
  public isCountrySelectVisible = false;
  public showGlobeIcon = false;
  public selectedSite: BehaviorSubject<ISiteListDetails> = new BehaviorSubject(null);
  public showHomepageSelectorDropDown = false;
  public positionsCountrySelectMenu = [];
  public showLoggedOutHomepage = false;
  public showLoggedOutBenefits = false;
  public showLoggedOutInspiration = false;
  public showDbsEligibility = false;
  public showTMRef1Eligibility = false;
  public enableRecaptcha: boolean;
  public navigationUrls: {
    id: string;
    name: string;
    isShowUrl: boolean;
    url: string;
    queryParams?: { v: string | null };
    eventName: string;
  }[] = [];
  private homepageUrl: string;

  @HostListener('window:resize', ['$event'])
  public resize() {
    this.applyMobileNav = isMobile() || isTablet();
  }

  constructor(
    private siteSettingsService: SiteSettingsService,
    private ngrxStore: Store<fromGlobalStore.State>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private languageService: LanguageService,
    private navigationService: NavigationService,
    @Optional() @Inject(CLIENT_PATH) public clientPath: ClientPath,
    private customisableVariablesService: CustomisableVariablesService,
    private siteConfService: SiteConfigService,
    public segment: Angulartics2Segment,
    private eligibilityRegistrationService: EligibilityRegistrationService,
    private preLoginService: PreloginService,
    private oauthService: OauthService
  ) {
    super(siteConfService, 'header');
    this.customisableVariables$ = this.customisableVariablesService.variables;
    this.applyMobileNav = isMobile() || isTablet();
    this.menuList$ = this.navigationService.getNavigationLinks();
    this.siteLogo$ = this.ngrxStore.select(fromGlobalStore.getSpecificCustomAsset, { customAsset: 'site_logo' }).pipe(
      filter((assets) => !!assets),
      map((assets) => assets[0]?.file || this.componentData?.logo)
    );
  }

  public ngOnInit(): void {
    this.positionsCountrySelectMenu =
      window.innerWidth > 1023
        ? [{ originX: 'start', originY: 'center', overlayX: 'start', overlayY: 'center' }]
        : [{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }];

    this.isUserLoggedIn = !!localStorage.getItem(USER_LOCAL_STORAGE_KEY);
    this.showGlobeIcon = this.siteConfService.getComponentData('header')?.default?.showGlobeNotCountryFlag;

    combineLatest([this.siteSettingsService.fetchSiteSettings(), this.siteConfService.siteList])
      .pipe(
        filter(([data, siteList]: [ISiteSettings, ISiteListDetails[]]) => data?.id && !!siteList.length),
        takeUntil(this.onDestroy$)
      )
      .subscribe(([data, siteList]: [ISiteSettings, ISiteListDetails[]]) => {
        this.siteSettingsLoaded = true;
        const userSelectedSite = Number(localStorage.getItem(USER_SELECTED_SITE));
        const selectedSiteURLOverride = this.siteSettingsService.getSiteIdFromURL();

        if (selectedSiteURLOverride) {
          this.siteSettingsService.syncSiteId(selectedSiteURLOverride);
        }

        const siteId = Number(selectedSiteURLOverride) || userSelectedSite;
        const selectedSiteDetails = siteList?.filter((site: ISiteListDetails) => {
          return site?.site_id === siteId;
        });

        if (userSelectedSite) {
          this.selectedSite.next(
            localStorage.getItem('selectedCountry')
              ? JSON.parse(localStorage.getItem('selectedCountry'))
              : selectedSiteDetails?.[0]
          );
        } else {
          this.setSite(siteList);
        }

        this.auth_provider = data?.authentication_provider?.provider;

        this.oauthActive = this.oauthService.isOauthEnabledSite();

        this.oauthLogInUrl = data.authentication_provider.login_url;
        this.showPlatformProvider = data.fe_configuration.show_platform_provider_nav;
        this.platformProviderType = data.fe_configuration.platform_provider_type;
        this.clientName = data.fe_configuration.name;
        this.isAccessHrt = data.switches.access_to_HTR;
        this.showHomepageSelectorDropDown = !data.switches.show_country_language_selector_page && siteList?.length > 1;
        this.showLoggedOutHomepage = data.switches.pre_login_home_page;
        this.showLoggedOutBenefits = this.showLoggedOutHomepage && data.switches.pre_login_benefits_page;
        this.showLoggedOutInspiration = this.showLoggedOutHomepage && data.switches.pre_login_inspiration_page;

        this.eligibilityType = data.eligibilities_and_selfregs;
        this.showSelfReg = !!this.siteConfService.getComponentData('homepage')?.default?.selfReg;
        this.showDbsEligibility = data.eligibilities_and_selfregs === EligibilitiesAndSelfRegEnum.TM_LINK_REF_1_REF_2;
        this.showTMRef1Eligibility = data.eligibilities_and_selfregs === EligibilitiesAndSelfRegEnum.TM_LINK_REF_1;
        this.enableRecaptcha = data?.switches?.enable_recaptchas && data?.switches?.enable_eligibility_recaptcha;
        const brandPath = data.fe_configuration.path;
        this.homepageUrl = `product/${brandPath}/homepage`;
        this.navigationUrls = [
          {
            id: 'home-page',
            name: 'PreLoginNavigation:Home',
            isShowUrl: this.showLoggedOutHomepage && (this.showLoggedOutBenefits || this.showLoggedOutInspiration),
            url: this.homepageUrl,
            queryParams: { v: '2' },
            eventName: 'Prelogin - Navigation Home Selected',
          },
          {
            id: 'benefits-page',
            name: 'PreLoginNavigation:Benefits',
            isShowUrl: this.showLoggedOutBenefits,
            url: 'product/benefits',
            queryParams: { v: null },
            eventName: 'Prelogin - Navigation Benefits Selected',
          },
          {
            id: 'inspiration-page',
            name: 'PreLoginNavigation:Inspiration',
            isShowUrl: this.showLoggedOutInspiration,
            url: 'product/inspiration',
            queryParams: { v: null },
            eventName: 'Prelogin - Navigation Inspiration Selected',
          },
        ];
      });

    this.router.events.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.showLogInBtn =
        this.componentData &&
        this.componentData.loginButton &&
        !this.hideLogInBtnRoutes.includes(window.location.pathname);

      this.pathname = window['location']['pathname'];
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.skipLinkPath = `${location.href.replace('#main-content', '')}#main-content`;
      });
  }

  public ngDoCheck(): void {
    this.currentRoute = window.location.pathname;
    if (
      this.currentRoute?.includes('/user/login') ||
      this.currentRoute?.includes('/user/forgotten-password') ||
      this.currentRoute?.includes('/registration')
    ) {
      this.showRegisterButton = false;
    } else {
      this.showRegisterButton = true;
    }
  }

  private setSite(sites?: ISiteListDetails[]): void {
    const singleSite = sites?.length === 1 ? sites?.[0] : null;
    const singleSiteId = singleSite ? sites?.[0]?.site_id : null;
    let browserCountry = (navigator.language.split('-')[1] || 'GB').toLowerCase();

    const decommissionedSite = this.siteConfService.getComponentData('redirectSite')?.default?.sites[0];

    if (decommissionedSite && decommissionedSite.locale === browserCountry) {
      browserCountry = 'GB';
      window.location.href = '/product/mastercard/homepage/gb/en-gb';
    }

    const sitesMatchingCountry = sites?.filter(
      (site: ISiteListDetails) => site?.country_code.toLowerCase() === browserCountry
    );
    let initPath = localStorage.getItem('initPath');
    const siteDetails = this.getCountryLanguage(sites, sitesMatchingCountry);
    const site =
      singleSite ||
      sites?.find(
        (item: ISiteListDetails) =>
          item?.country_code?.toLowerCase() === siteDetails?.country.toLowerCase() &&
          item?.language?.toLowerCase() === siteDetails?.lang.toLowerCase()
      );
    const siteId: number = this.siteSettingsService.getSiteIdFromURL() || singleSiteId || site.site_id;
    const selectedSiteDetails = singleSite || sites?.filter((item: ISiteListDetails) => item?.site_id === siteId);
    const pathname = window.location.pathname;
    const feConfigPath = this.siteSettingsService?.settings?.fe_configuration?.path;
    let brandPath = this.siteSettingsService.replaceBrandPath(feConfigPath);
    brandPath = this.siteSettingsService.brandPathOverride(brandPath, siteDetails?.country);

    this.selectedSite.next(selectedSiteDetails?.[0]);

    if ((pathname.includes('/homepage') || pathname === '/') && sites?.length > 1) {
      const homeUrl = `/product/${brandPath}/homepage/${site.country_code.toLowerCase()}/${site.language}`;
      window.location.href = homeUrl;
      return;
    }

    if (initPath) {
      localStorage.removeItem('initPath');
      this.siteSettingsService.syncSiteId(siteId);
      window.history.replaceState({}, document.title, window.location.href);
      //TPD-27886 - initPath contained window.location.pathname and therefore dropped all query params
      initPath = window.location.href;
      window.location.href = initPath;
    }
  }

  private getCountryLanguage(sites, siteMatch): { country: string; lang: string } {
    let country = '';
    let lang = '';

    if (sites.length === 1) {
      country = sites[0]?.country_code?.toLowerCase();
      lang = sites[0]?.language?.toLowerCase();
      return { country, lang };
    }

    if (siteMatch?.length > 0) {
      country = siteMatch?.[0]?.country_code?.toLowerCase();
      lang = siteMatch?.[0]?.language?.toLowerCase();
    } else {
      country = sites?.find((site) => site.country_code.toLowerCase() === 'gb')
        ? 'gb'
        : sites?.find((site) => site.language === 'en-gb').country_code.toLowerCase();
      lang = 'en-gb';
    }
    return { country, lang };
  }

  public toggleBurgerMenu(visible: boolean): void {
    const delay = !visible ? 400 : 0;
    setTimeout(() => {
      this.showMobileNavOverlay = visible;
    }, delay);
    setTimeout(() => {
      if (this.languageService.isRtlLanguage()) {
        this.menuState = visible === true ? 'in-rtl' : 'out-rtl';
      } else {
        this.menuState = visible === true ? 'in' : 'out';
      }
    }, 0);
  }

  public openEligibilityModal(): void {
    this.eligibilityRegistrationService.getEligibilityCheckType(this.eligibilityType);
    this.toggleBurgerMenu(false);
    this.preLoginService.eventTrack('Prelogin - Register now CTA selected', { area: 'Navigation' });
  }

  /**
   * Transport the user to either the login route or the external oauth login page depending
   * on whether oauth is active. Called when the user clicks onto the login button
   */
  public loginLink(): void {
    this.toggleBurgerMenu(false);
    this.preLoginService.eventTrack('Prelogin - Login CTA selected', { area: 'Navigation' });
    const redirectUrl = this.activatedRoute.snapshot.queryParams['returnUrl'];
    if (this.oauthActive) {
      window.location.href = `${window.location.origin}${this.oauthLogInUrl || ''}?onwardUrl=${
        redirectUrl || '/user/login'
      }`;
    } else {
      this.router.navigate(['/user/login']);
    }
  }

  public logoClick(action: 'homepage' | 'refresh'): void {
    if (
      action === 'refresh' &&
      this.componentData?.pagesToRefreshAfterLogoClick?.find((page) => page === this.router.url)
    ) {
      window.location.reload();
    } else {
      this.router.navigate([this.homepageUrl], { queryParams: { v: '2' } });
    }
  }

  public updateSite(value: ISiteListDetails) {
    this.preLoginService.eventTrack('Prelogin - Navigation Language selected', {
      language: value.language,
      country: value.country_code,
    });
    this.selectedSite.next(value);
  }

  public trackNavigation(eventName: string): void {
    this.preLoginService.eventTrack(eventName);
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
